import React, {
  ReactElement,
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useMemo,
} from 'react'
import { useDispatch, useSelector } from 'react-redux'

import { RootState } from '~redux/rootReducer'
import { setSelectedEIOrganization } from '~redux/slices/organizationSlice'
import { AppDispatch } from '~redux/store'
import { EmailIdentity } from '~types'

export interface AISettingsContextType {
  selectedOrganization: EmailIdentity | null
  setSelectedOrganization: (organization: EmailIdentity) => void
}

const AISettingsContext = createContext<AISettingsContextType | undefined>(undefined)

export const useAISettings = (): AISettingsContextType => {
  const context = useContext(AISettingsContext)
  if (!context) {
    throw new Error('useAISettings must be used within an AISettingsProvider')
  }
  return context
}

interface AISettingsProviderProps {
  children: ReactNode
}

export const AISettingsProvider = ({ children }: AISettingsProviderProps): ReactElement => {
  const dispatch: AppDispatch = useDispatch()
  const selectedOrganization = useSelector(
    (state: RootState) => state?.organization.selectedOrganization,
  )

  const setSelectedOrganization = useCallback(
    (organization: EmailIdentity) => {
      if (organization.id !== selectedOrganization?.id) {
        dispatch(setSelectedEIOrganization(organization))
      }
    },
    [dispatch, selectedOrganization],
  )

  const memoizedSelectedOrganization = useMemo(() => selectedOrganization, [selectedOrganization])

  const value = useMemo(
    () => ({
      selectedOrganization: memoizedSelectedOrganization ?? null,
      setSelectedOrganization,
    }),
    [memoizedSelectedOrganization, setSelectedOrganization],
  )

  return <AISettingsContext.Provider value={value}>{children}</AISettingsContext.Provider>
}
